/**
 * <tecton-quiz> — weighted product recommendation quiz.
 *
 * Port of ProductQuiz.tsx. Each answer carries "handle:points" pairs; totals
 * accumulate across questions and the highest total wins. Ties break in favour
 * of whichever result block appears first in the DOM, which is why the section
 * documents that ordering as meaningful.
 *
 * Uses a native <dialog>, so focus trapping, Escape-to-close, inert background
 * and the top layer are the browser's job rather than ours.
 *
 * History is tracked as a stack of answered questions so Back can un-apply the
 * scores from the answer it is reversing — recomputing from scratch would need
 * the whole answer set kept anyway, and this keeps the score object honest.
 */

class TectonQuiz extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.steps = [...this.querySelectorAll('[data-step]')];
    this.results = [...this.querySelectorAll('[data-result]')];
    this.progress = this.querySelector('[data-quiz-progress]');
    this.backButton = this.querySelector('[data-quiz-back]');

    this.total = Number(this.dataset.questionCount) || this.steps.length;

    this.reset();

    for (const step of this.steps) {
      for (const answer of step.querySelectorAll('[data-scores]')) {
        answer.addEventListener('click', () => this.answer(answer.dataset.scores));
      }
    }

    this.backButton?.addEventListener('click', () => this.back());
    this.querySelector('[data-quiz-close]')?.addEventListener('click', () => this.dialog.close());
    for (const button of this.querySelectorAll('[data-quiz-restart]')) {
      button.addEventListener('click', () => this.reset());
    }

    // Delegated so triggers rendered later — the mega menu, a lazily loaded
    // section — still work without re-binding.
    this.onDocumentClick = (event) => {
      const trigger = event.target.closest?.('[data-tecton-quiz-open]');
      if (!trigger) return;
      event.preventDefault();
      this.reset();
      this.dialog.showModal();
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  reset() {
    this.scores = new Map();
    this.history = [];
    this.index = 0;
    this.render();
  }

  /** Parses "handle:2, other:1" into [[handle, 2], [other, 1]]. */
  parse(raw) {
    return (raw || '')
      .split(',')
      .map((pair) => pair.split(':'))
      .filter((parts) => parts.length === 2)
      .map(([handle, points]) => [handle.trim(), Number(points)])
      .filter(([handle, points]) => handle && Number.isFinite(points));
  }

  answer(raw) {
    const pairs = this.parse(raw);
    for (const [handle, points] of pairs) {
      this.scores.set(handle, (this.scores.get(handle) || 0) + points);
    }
    this.history.push(pairs);
    this.index += 1;
    this.render();
  }

  back() {
    const previous = this.history.pop();
    if (!previous) return;

    for (const [handle, points] of previous) {
      this.scores.set(handle, (this.scores.get(handle) || 0) - points);
    }
    this.index -= 1;
    this.render();
  }

  /** Highest score wins; DOM order of the result panes breaks ties. */
  winner() {
    let best = null;
    let bestScore = -Infinity;

    for (const pane of this.results) {
      const handle = pane.dataset.result;
      const score = this.scores.get(handle) || 0;
      if (score > bestScore) {
        bestScore = score;
        best = pane;
      }
    }
    return best;
  }

  render() {
    const finished = this.index >= this.total;

    for (const step of this.steps) {
      step.hidden = finished || Number(step.dataset.step) !== this.index;
    }

    const winner = finished ? this.winner() : null;
    for (const pane of this.results) {
      pane.hidden = pane !== winner;
    }

    if (this.progress) {
      const pct = this.total === 0 ? 100 : Math.round((this.index / this.total) * 100);
      this.progress.style.width = `${pct}%`;
    }

    if (this.backButton) this.backButton.hidden = this.index === 0;

    // Re-trigger the entrance animation on whichever pane just became visible.
    const visible = finished ? winner : this.steps[this.index];
    if (visible) {
      visible.classList.remove('quiz-step');
      void visible.offsetWidth;
      visible.classList.add('quiz-step');
    }
  }
}

if (!customElements.get('tecton-quiz')) {
  customElements.define('tecton-quiz', TectonQuiz);
}
